import axios from "axios";
import i18next from "i18next";
import { getFileTooLargeMessage } from "./fileUpload";

const PAYLOAD_TOO_LARGE_STATUS = 413;

const getFallbackErrorMessage = () =>
  i18next.t("security.error.refresh", {
    defaultValue:
      "Greška, pokušajte da osvežite stranu pa ponovo pokušajte istu akciju.",
  });

const handleErrors = async (error) => {
  const { response } = error || {};
  const { status } = response || {};

  if (status === 401) {
    window.location.href = "/";
    localStorage.removeItem("loggedUser");
  } else {
    await normalizeErrorResponse(error);
    throw error;
  }
};

const normalizeErrorResponse = async (error) => {
  if (!error?.response) return;

  const { response } = error;
  const { status } = response;

  if (response.data instanceof Blob) {
    response.data = await parseErrorBody(await response.data.text(), status);
    return;
  }

  response.data = await parseErrorBody(response.data, status);
};

const parseErrorBody = async (data, status) => {
  if (status === PAYLOAD_TOO_LARGE_STATUS) {
    return normalizePayloadTooLargeMessage(data);
  }

  if (typeof data === "string") {
    const trimmedData = data.trim();

    if (!trimmedData || trimmedData.startsWith("<")) {
      return { message: getFallbackErrorMessage() };
    }

    try {
      const parsedData = JSON.parse(trimmedData);
      return parsedData?.message ? parsedData : { message: trimmedData };
    } catch {
      return { message: trimmedData };
    }
  }

  if (data?.message) return data;

  return { message: getFallbackErrorMessage() };
};

const normalizePayloadTooLargeMessage = (data) => {
  if (data?.message) return data;

  if (typeof data === "string") {
    const trimmedData = data.trim();

    if (trimmedData && !trimmedData.startsWith("<")) {
      try {
        const parsedData = JSON.parse(trimmedData);
        if (parsedData?.message) return parsedData;
      } catch {
        return { message: trimmedData };
      }
    }
  }

  // Ako proxy/backend vrati prazan ili HTML 413, korisnik dobija jasnu poruku.
  return { message: getFileTooLargeMessage() };
};

const apiService = {
  get(url) {
    return axios
      .get(url, {
        headers: getSimpleHeaders(),
      })
      .catch(handleErrors);
  },
  post(url, body) {
    return axios.post(url, body, {
      headers: getSimpleHeaders(),
    });
  },
  postMultipart(url, body) {
    return axios.post(url, body, {
      headers: getSimpleHeadersFile(),
    });
  },
  putMultipart(url, body) {
    return axios.put(url, body, {
      headers: getSimpleHeadersFile(),
    });
  },
  patch(url, body) {
    return axios.patch(url, body, {
      headers: getSimpleHeaders(),
    });
  },
  put(url, body) {
    return axios.put(url, body, {
      headers: getSimpleHeaders(),
    });
  },
  delete(url) {
    return axios.delete(url, {
      headers: getSimpleHeaders(),
    });
  },

  getFromFile(url) {
    return axios.get(url, {
      responseType: "blob",
      headers: getSimpleHeaders(),
    });
  },
};

function getSimpleHeaders() {
  const loggedUser = JSON.parse(localStorage.getItem("loggedUser"));
  const token = loggedUser?.token;
  return {
    "Content-Type": "application/json",
    "Accept-Language": i18next.language,
    Authorization: `${token}`,
  };
}

function getSimpleHeadersFile() {
  const loggedUser = JSON.parse(localStorage.getItem("loggedUser"));
  const token = loggedUser?.token;
  return {
    // Browser mora sam da doda multipart boundary uz FormData.
    "Accept-Language": i18next.language,
    Authorization: `${token}`,
  };
}

export default apiService;

// Interceptor za proveru verzije aplikacije
axios.interceptors.response.use(
  (response) => {
    const serverVersion = response.headers["x-application-version"];
    let currentVersion = localStorage.getItem("appVersion");

    // Ako nema lokalne verzije, postavi je tek ako backend vrati verziju
    if (!currentVersion && serverVersion) {
      localStorage.setItem("appVersion", serverVersion);
      return response;
    }

    // Ako postoji razlika u verzijama i nismo na login stranici
    const isLoginRoute = window.location.pathname === "/login";

    if (
      serverVersion &&
      serverVersion !== currentVersion &&
      !isLoginRoute // važno: ne radimo reload na login stranici
    ) {
      localStorage.setItem("appVersion", serverVersion);
      window.location.reload();
    }

    return response;
  },
  async (error) => {
    return await handleErrors(error);
  }
);
